import type { GetServerSideProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { StoryCardData, StoryTitle } from '@/prakrit/components/stories/story-logic'

import Link from 'next/link'
import { ArrowRight, BookOpenText } from 'lucide-react'
import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { Bi } from '@/prakrit/components/common/bi'
import { CONTAINER, hiDigits } from '@/prakrit/components/grammar/labs/lab-ui'
import { StoryCard } from '@/prakrit/components/stories/story-card'
import { HubHeader, ReadingTips, StoriesTrustNote } from '@/prakrit/components/stories/story-hub'
import { STORIES } from '@/prakrit/lib/content/stories'
import { getSessionUserSafe } from '@/lib/server/auth'
import { canPreviewLessons } from '@/prakrit/lib/server/lessons'
import { storyCard, storyStatuses } from '@/prakrit/components/stories/story-server'
import { accessOf } from '@/lib/server/access'

interface DialoguesPageProps {
    dialogues: StoryCardData[]
    storyTitles: StoryTitle[]
}

const DialoguesPage: FC<DialoguesPageProps> = ({ dialogues, storyTitles }): ReactNode => {
    const readable = dialogues.filter(d => d.visible || d.preview).length

    return (
        <Layout>
            <Seo
                title='बातचीत'
                description='Short Prakrit dialogues with the speaker of every line, every word tappable, Hindi and English translations, questions and sentence ordering.'
            />

            <div className={CONTAINER}>
                <HubHeader
                    eyebrow='प्राकृत में बातचीत'
                    title={{ hi: 'बातचीत', en: 'Dialogues' }}
                    intro={{
                        hi: 'दो लोगों के बीच छोटी बातचीत। हर पंक्ति के साथ बोलने वाले का नाम है; किसी भी शब्द पर टैप करके उसका विश्लेषण देखिए।',
                        en: 'Short conversations between two people. Every line shows who is speaking; tap any word to see its analysis.'
                    }}
                    kidsIntro={{
                        hi: 'दो दोस्त प्राकृत में बात करते हैं। तुम भी पढ़ो!',
                        en: 'Two friends talk in Prakrit. Read along!'
                    }}
                    counts={[{ n: dialogues.length, hi: 'बातचीत', en: dialogues.length === 1 ? 'dialogue' : 'dialogues' }]}
                    back={{ href: '/stories', hi: 'कथाएँ', en: 'Stories' }}
                />

                <ReadingTips />

                <div className='leaf-rule my-10' aria-hidden='true' />

                <section aria-labelledby='dialogues-heading'>
                    <h2 id='dialogues-heading' className='flex flex-wrap items-baseline gap-x-3'>
                        <span lang='hi' className='text-2xl font-bold sm:text-[1.75rem]'>सभी बातचीत</span>
                        <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>All dialogues</span>
                    </h2>
                    {readable === 0 && dialogues.length > 0 && (
                        <p className='mt-3 max-w-3xl text-sm'>
                            <Bi
                                hi='अभी कोई बातचीत पढ़ने के लिए नहीं खुली है। बातचीत जल्द ही यहाँ खुलेंगी।'
                                en='No dialogue is open for reading yet. They will open here soon.'
                            />
                        </p>
                    )}
                    {dialogues.length === 0 ? (
                        <p className='mt-6 rounded-xl border border-dashed border-border p-5 text-sm'>
                            <Bi hi='अभी कोई बातचीत नहीं है।' en='There are no dialogues yet.' />
                        </p>
                    ) : (
                        <ul className='mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
                            {dialogues.map(dialogue => (
                                <li key={dialogue.id}>
                                    <StoryCard story={dialogue} />
                                </li>
                            ))}
                        </ul>
                    )}
                </section>

                <section aria-label='कथाएँ · Stories' className='pt-12'>
                    <Link
                        href='/stories'
                        className='group flex flex-col gap-4 rounded-xl border border-border bg-card p-5 shadow-card transition-colors hover:border-saffron/60 sm:flex-row sm:items-center sm:p-6'
                    >
                        <span aria-hidden='true' className='flex size-14 shrink-0 items-center justify-center rounded-2xl bg-secondary text-saffron-strong'>
                            <BookOpenText className='size-7' />
                        </span>
                        <span className='flex min-w-0 flex-1 flex-col gap-1'>
                            <span className='flex flex-wrap items-baseline gap-x-3'>
                                <span lang='hi' className='hindi text-xl font-bold'>कथाएँ</span>
                                <span lang='en' className='font-sans text-base font-medium text-muted-foreground'>Stories</span>
                            </span>
                            <Bi
                                hi={`${hiDigits(storyTitles.length)} क्रमबद्ध कथाएँ, हर शब्द टैप करने योग्य।`}
                                en={`${storyTitles.length} graded stories, every word tappable.`}
                                className='gap-0.5 text-[0.9375rem] leading-relaxed'
                            />
                            {storyTitles.length > 0 && (
                                <span className='flex flex-wrap gap-2 pt-1'>
                                    {storyTitles.map(t => (
                                        <span key={`${t.pra}-${t.en}`} className='inline-flex items-baseline gap-1.5 rounded-full bg-secondary px-3 py-1 text-sm'>
                                            <span lang='pra' className='font-prakrit font-semibold text-prakrit'>{t.pra}</span>
                                            <span lang='en' className='text-xs text-muted-foreground'>{t.en}</span>
                                        </span>
                                    ))}
                                </span>
                            )}
                        </span>
                        <span className='inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-prakrit'>
                            <span lang='hi' className='hindi'>कथाएँ देखें</span>
                            <span aria-hidden='true' className='text-muted-foreground'>·</span>
                            <span lang='en'>See the stories</span>
                            <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />
                        </span>
                    </Link>
                </section>

                <StoriesTrustNote />
            </div>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<DialoguesPageProps> = async ({ req }) => {
    const [statusOf, user] = await Promise.all([storyStatuses(), getSessionUserSafe(req)])
    const reviewer = canPreviewLessons(user)
    const access = await accessOf(user)

    return {
        props: {
            dialogues: STORIES.filter(s => s.kind === 'dialogue').map(story => storyCard(story, statusOf(story.id), reviewer, access.full)),
            storyTitles: STORIES.filter(s => s.kind === 'story').map(s => ({ ...s.title }))
        }
    }
}

export default DialoguesPage
